"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import {
  BsArrowsMove,
  BsCalendarDate,
  BsCheckCircleFill,
  BsDownload,
  BsPrinter,
  BsStars,
  BsTrash,
} from "react-icons/bs";
import { generateIcon } from "utils";
import { useMediaContext } from "../../../context/MediaContext";
import { getUser } from "../../../services";

interface ISelectableWrapperProps {
  children: React.ReactNode;
  items: string[]; // itemIds
  handleDownload: (downloadAll: boolean) => void;
  handleDelete: () => void;
  handleSetMediaDate: () => void;
  showDelete: boolean;
  handleMove: () => void;
  handlePrint: () => void;
  printerAvailable: boolean;
  showPremiumServices?: boolean;
  onPremiumServicesClick?: () => void;
}

export default function SelectableWrapper({
  children,
  items,
  handleDownload,
  handleDelete,
  handleSetMediaDate,
  showDelete,
  handleMove,
  handlePrint,
  printerAvailable,
  showPremiumServices,
  onPremiumServicesClick,
}: ISelectableWrapperProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const { selectedItems, toggleAll } = useMediaContext();
  const t = useTranslations();
  const getToggleAllButtonClassName = () => {
    const color =
      selectedItems.length === items.length && items.length > 0
        ? "text-purple-950"
        : "text-gray-400";

    return `cursor-pointer ${color}`;
  };

  useEffect(() => {
      getUser()
        .then((usr) => setIsAdmin(usr.data.isAdmin))
        .catch(() => setIsAdmin(false));
  }, []);
  if (items.length === 0) {
    return "";
  }

  return (
    <section>
      <div
        className={`${
          selectedItems.length === 0 ? "justify-end" : ""
        } card bg-base-200/50 rounded-lg p-2 flex flex-row items-center`}
      >
        <div className="pl-1">
          <span
            className={getToggleAllButtonClassName()}
            onClick={() => {
              toggleAll(items);
            }}
          >
            {generateIcon(BsCheckCircleFill, "text-[16px] scale-[1.35]")}
          </span>
        </div>

        {selectedItems.length > 0 ? (
          <div className="flex max-w-[65px] lg:max-w-full ml-2 lg:ml-3">
            <span className="text-gray-700 text-[12px] lg:text-[15px] mr-auto font-semibold whitespace-normal lg:whitespace-nowrap">
              {t("Gallery.itemSelected", { selected: selectedItems.length })}
            </span>
          </div>
        ) : null}

        <div className="w-full flex flex-wrap justify-end gap-2">
          {selectedItems.length > 0 && (
            <>
              {/* 1. Prémium szolgáltatások */}
              {showPremiumServices ? (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary tracking-[-0.03em] lg:tracking-normal"
                  onClick={() => onPremiumServicesClick?.()}
                >
                  {generateIcon(BsStars, "h-[18px] w-[18px]")}{" "}
                  {t("Gallery.premiumServices")}
                </button>
              ) : null}

              {/* 2. Törlés */}
              {showDelete ? (
                <button
                  type="button"
                  className="btn btn-sm btn-error text-white mb-[5px] lg:mb-0 tracking-[-0.03em] lg:tracking-normal"
                  onClick={() => handleDelete()}
                >
                  {generateIcon(BsTrash, "h-[18px] w-[18px]")}{" "}
                  {t("Gallery.deleteSelected")}
                </button>
              ) : null}
              {/* 3. Nyomtatás */}
              {showDelete && printerAvailable ? (
                <button
                  type="button"
                  className="btn btn-sm btn-icon lg:ml-[15px] mb-[5px] lg:mb-0 tracking-[-0.03em] lg:tracking-normal"
                  onClick={() => handlePrint()}
                >
                  {generateIcon(BsPrinter, "h-[18px] w-[18px]")}{" "}
                  {t("Gallery.printSelected")}
                </button>
              ) : null}

              {/* 4. Kiválasztottak letöltése */}
              <button
                type="button"
                className="btn btn-sm btn-icon mb-[5px] lg:mb-0 tracking-[-0.03em] lg:tracking-normal"
                onClick={() => handleDownload(false)}
              >
                {generateIcon(BsDownload, "h-[20px] w-[20px]")}{" "}
                {t("Gallery.downloadSelected")}
              </button>
            </>
          )}

          {/* 4. Összes letöltése (mindig látható) */}
          <button
            type="button"
            className="btn btn-sm btn-primary tracking-[-0.03em] lg:tracking-normal"
            onClick={() => handleDownload(true)}
          >
            {t("Gallery.downloadAllSelected")}
          </button>

          {selectedItems.length > 0 && (
            <>
              {/* 5. Áthelyezés (csak admin) */}
              {isAdmin ? (
                <button
                  type="button"
                  className="btn btn-sm btn-icon mb-[5px] lg:mb-0 tracking-[-0.03em] lg:tracking-normal"
                  onClick={() => handleMove()}
                >
                  {generateIcon(BsArrowsMove, "h-[18px] w-[18px]")}{" "}
                  {t("Gallery.moveSelected")}
                </button>
              ) : null}

              {/* 6. Dátum állítás */}
              <button
                type="button"
                className="btn btn-sm btn-icon tracking-[-0.03em] lg:tracking-normal"
                onClick={() => handleSetMediaDate()}
              >
                {generateIcon(BsCalendarDate, "h-[18px] w-[18px]")}{" "}
                {t("Gallery.setNewDateForSelected")}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="relative transition-all pt-2 lg:pt-[30px]">
        {children}
      </div>
    </section>
  );
}